import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Measured Livestock | Login';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f4f0] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-[#1a4d2e] hover:text-[#ff6b00]"
          data-testid="back-to-home-button"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-2 border-[#1a4d2e]">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="font-heading text-3xl text-[#1a4d2e]">
                Customer Login
              </CardTitle>
              <CardDescription className="text-[#8d6e63]">
                Access your bookings and quote history
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[#1a4d2e] font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[#1a4d2e] font-medium">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="mt-2 h-12 border-[#e5ddd5] focus:border-[#ff6b00] focus:ring-[#ff6b00]"
                    data-testid="password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white font-heading text-lg"
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    'Logging in...'
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Log In
                    </>
                  )}
                </Button>

                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-[#1a4d2e] hover:text-[#ff6b00] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>

              <div className="mt-6">
                <div className="p-4 bg-[#f4f4f0] rounded-md">
                  <p className="text-sm text-[#4a4a4a]">
                    Your account is created after your first booking is confirmed. Check your email for login credentials.
                  </p>
                </div>
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm text-[#8d6e63]">
                  Don't have an account yet?{' '}
                  <button
                    onClick={() => navigate('/book')}
                    className="text-[#ff6b00] hover:underline font-semibold"
                    data-testid="book-now-link"
                  >
                    Book your first service
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
